import React, { useMemo, useState } from 'react'

import {
    Button,
    Chip,
    IconButton,
    InputAdornment,
    ListSubheader,
    MenuItem,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import PriorityHighIcon from '@material-ui/icons/PriorityHigh'
import RoomIcon from '@material-ui/icons/Room'
import MessageIcon from '@material-ui/icons/Message'
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary'
import CloseIcon from '@material-ui/icons/Close'
import FlagIcon from '@material-ui/icons/Flag'
import HomeIcon from '@material-ui/icons/Home'
import AllOutIcon from '@material-ui/icons/AllOut'
import PeopleIcon from '@material-ui/icons/People'
import { ReactComponent as ActionItemIcon } from '../../../assets/icons/action-item.svg'

import { Container } from '../../../components'
import {
    ActionItemSuggestion,
    SuggestionDecision,
} from '../../../hooks/useActionItemSuggestions/types'
import { typographyStyle } from '../../../theme/typographyStyle'
import {
    ApartmentVendor,
    getServiceImageDetails,
    Service,
    User,
} from '../../../models'

const MAX_NOTE_LENGTH = 140
const MAX_VISIBLE_PHOTOS = 3
const PHOTO_WIDTH = 240
const PHOTO_HEIGHT = 180

const getSuggestionTitle = (suggestion: ActionItemSuggestion): string => {
    const { area_name, area_label } = suggestion.source_area
    const { inventory_name } = suggestion.source_inventory_item
    const areaPrefix =
        area_name === 'Com' ? area_name : `${area_name} - ${area_label}`
    return `${areaPrefix} | ${inventory_name}`
}

interface ActionItemSuggestionCardProps {
    suggestion: ActionItemSuggestion
    decision: SuggestionDecision
    selectedServiceId: number | null
    selectedVendorId: number | null
    serviceList: Service[]
    apartmentVendorList: ApartmentVendor[]
    onAccept: () => void
    onReject: () => void
    onSelectService: (serviceId: number) => void
    onSelectVendor: (vendorId: number) => void
    myTeamMode: boolean
    onToggleMyTeam: () => void
    userList: User[]
    selectedUserId: number | null
    onSelectUser: (userId: number) => void
    isBulkMode?: boolean
    isSelected?: boolean
    onToggleSelect?: () => void
}

export const ActionItemSuggestionCard: React.FC<ActionItemSuggestionCardProps> = ({
    suggestion,
    decision,
    selectedServiceId,
    selectedVendorId,
    serviceList,
    apartmentVendorList,
    onAccept,
    onReject,
    onSelectService,
    onSelectVendor,
    myTeamMode,
    onToggleMyTeam,
    userList,
    selectedUserId,
    onSelectUser,
    isBulkMode = false,
    isSelected = false,
    onToggleSelect,
}) => {
    const theme = useTheme()
    const effectiveDecision =
        (decision?.toLowerCase() as SuggestionDecision) ?? 'pending'
    const [photosExpanded, setPhotosExpanded] = useState(false)
    const [hoveredButton, setHoveredButton] = useState<
        'accept' | 'reject' | null
    >(null)
    const [serviceSearch, setServiceSearch] = useState('')
    const [vendorSearch, setVendorSearch] = useState('')

    const selectedService = useMemo(
        () =>
            suggestion.suggested_services.find(
                (s) => s.service_id === selectedServiceId,
            ),
        [suggestion.suggested_services, selectedServiceId],
    )

    const suggestedServiceIds = useMemo(
        () => new Set(suggestion.suggested_services.map((s) => s.service_id)),
        [suggestion.suggested_services],
    )

    const allServices = useMemo(() => {
        const suggested = suggestion.suggested_services.map((s) => ({
            id: s.service_id,
            name: s.service_name,
            service_category: s.service_category,
            confidence: s.confidence,
        }))
        const extra = serviceList
            .filter((s) => !suggestedServiceIds.has(s.id))
            .map((s) => ({
                id: s.id,
                name: s.name,
                service_category: s.service_category,
                confidence: null as number | null,
            }))
        return [...suggested, ...extra]
    }, [suggestion.suggested_services, serviceList, suggestedServiceIds])

    const suggestedVendorIds = useMemo(() => {
        const ids = new Set<number>()
        selectedService?.suggested_vendors.forEach((v) => ids.add(v.vendor_id))
        return ids
    }, [selectedService])

    const allVendorsForService = useMemo(() => {
        const suggested = (selectedService?.suggested_vendors ?? []).map(
            (v) => ({
                id: v.vendor_id,
                name: v.vendor_name,
                confidence: v.confidence,
                in_house: v.in_house,
            }),
        )
        const extra = apartmentVendorList
            .filter(
                (av) =>
                    !suggestedVendorIds.has(av.vendor.id) &&
                    av.vendor.services.some((s) => s.id === selectedServiceId),
            )
            .map((av) => ({
                id: av.vendor.id,
                name: av.vendor.name,
                confidence: null as number | null,
                in_house: !!av.vendor.in_house,
            }))
        return [...suggested, ...extra]
    }, [
        selectedService,
        apartmentVendorList,
        suggestedVendorIds,
        selectedServiceId,
    ])

    const filteredServices = useMemo(() => {
        if (!serviceSearch.trim()) return allServices
        const q = serviceSearch.toLowerCase()
        return allServices.filter((s) => s.name.toLowerCase().includes(q))
    }, [allServices, serviceSearch])

    const filteredVendors = useMemo(() => {
        if (!vendorSearch.trim()) return allVendorsForService
        const q = vendorSearch.toLowerCase()
        return allVendorsForService.filter((v) =>
            v.name.toLowerCase().includes(q),
        )
    }, [allVendorsForService, vendorSearch])

    const confidencePercent = Math.round(suggestion.confidence * 100)

    const inspectorMessages =
        suggestion.source_inventory_item.inspector_messages
    const photoUrls = suggestion.source_inventory_item.inspector_photo_urls
    const hasOverflowPhotos = photoUrls.length > MAX_VISIBLE_PHOTOS
    const visiblePhotos = photosExpanded
        ? photoUrls
        : photoUrls.slice(0, MAX_VISIBLE_PHOTOS)
    const overflowCount = photoUrls.length - MAX_VISIBLE_PHOTOS

    const fullNoteText = inspectorMessages.map((msg) => msg.content).join('; ')
    const noteTruncated = fullNoteText.length > MAX_NOTE_LENGTH
    const displayNoteText = noteTruncated
        ? fullNoteText.slice(0, MAX_NOTE_LENGTH) + '...'
        : fullNoteText

    const formatMessageDate = (isoDate: string): string => {
        const d = new Date(isoDate)
        const date = d.toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: '2-digit',
        })
        const time = d.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        return `${date} ${time}`
    }

    const borderColor =
        effectiveDecision === 'rejected'
            ? theme.palette.error.main
            : theme.palette.grey[300]

    const backgroundColor =
        effectiveDecision === 'rejected' ? theme.palette.grey[100] : 'white'

    const photoThumbnail = (url: string, idx: number) => (
        <Tooltip
            key={idx}
            placement="left"
            interactive
            title={
                <img
                    src={url}
                    alt={`Inspection photo ${idx + 1}`}
                    style={{
                        maxWidth: 720,
                        maxHeight: 540,
                        borderRadius: 4,
                        display: 'block',
                    }}
                />
            }
        >
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'block', flexShrink: 0 }}
            >
                <img
                    src={url}
                    alt={`Inspection photo ${idx + 1}`}
                    style={{
                        width: PHOTO_WIDTH,
                        height: PHOTO_HEIGHT,
                        objectFit: 'cover',
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.15s ease',
                    }}
                />
            </a>
        </Tooltip>
    )

    const bulkBorder =
        isBulkMode && isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : isBulkMode
            ? '2px dashed rgba(0,0,0,0.25)'
            : `2px solid ${borderColor}`

    const bulkBg =
        isBulkMode && isSelected ? `rgba(25,118,210,0.06)` : backgroundColor

    return (
        <div
            onClick={isBulkMode ? onToggleSelect : undefined}
            style={{
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                backgroundColor: bulkBg,
                borderRadius: theme.shape.borderRadius,
                border: bulkBorder,
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                opacity: effectiveDecision === 'rejected' ? 0.7 : 1,
                cursor: isBulkMode ? 'pointer' : undefined,
            }}
        >
            {/* Header */}
            <Container
                style={{
                    padding: theme.spacing(1.5, 2),
                    alignItems: 'center',
                    gap: theme.spacing(1.5),
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                {/* Confidence Badge */}
                <Tooltip title="Confidence that this suggestion should be turned into an action item and the pre-selected service and vendor are correct">
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 52,
                            height: 52,
                            borderRadius: theme.shape.borderRadius,
                            backgroundColor:
                                confidencePercent >= 75
                                    ? 'rgba(76,175,80,0.4)'
                                    : confidencePercent >= 50
                                    ? 'rgba(255,152,0,0.4)'
                                    : 'rgba(244,67,54,0.4)',
                            flexShrink: 0,
                        }}
                    >
                        <span
                            style={{
                                fontSize: '1.1rem',
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: 'rgba(0,0,0,0.87)',
                            }}
                        >
                            {confidencePercent}%
                        </span>
                        <span
                            style={{
                                fontSize: '0.6rem',
                                fontWeight: 500,
                                lineHeight: 1,
                                color: 'rgba(0,0,0,0.87)',
                                textTransform: 'uppercase',
                                letterSpacing: 0.3,
                            }}
                        >
                            conf.
                        </span>
                    </div>
                </Tooltip>

                {/* Icon Section */}
                <Container
                    style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing(0.75),
                        flexShrink: 0,
                        minWidth: 80,
                        maxWidth: 80,
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            display: 'inline-flex',
                            width: 26,
                            height: 26,
                            flexShrink: 0,
                        }}
                    >
                        {effectiveDecision === 'accepted' && (
                            <Tooltip title="Accepted">
                                <div
                                    style={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        width: 26,
                                        height: 26,
                                    }}
                                >
                                    <ActionItemIcon
                                        style={{ width: 26, height: 26 }}
                                    />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                    {suggestion.priority && (
                        <Tooltip title="Priority">
                            <PriorityHighIcon
                                style={{
                                    color: theme.palette.error.main,
                                    fontSize: 22,
                                }}
                            />
                        </Tooltip>
                    )}
                    {suggestion.source_inventory_item
                        .inspector_status_causes_flag && (
                        <Tooltip title="Flagged">
                            <FlagIcon
                                style={{
                                    color: theme.palette.secondary.main,
                                    fontSize: 22,
                                }}
                            />
                        </Tooltip>
                    )}
                </Container>

                {/* Title & Location */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        gap: 2,
                    }}
                >
                    <Container style={{ alignItems: 'center', gap: 8 }}>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 600,
                                fontSize: '1rem',
                            }}
                        >
                            {getSuggestionTitle(suggestion)}
                        </span>
                        <Chip
                            label={
                                suggestion.source_inventory_item
                                    .inspector_status
                            }
                            size="small"
                            style={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                borderColor:
                                    suggestion.source_inventory_item
                                        .inspector_status_color || undefined,
                                borderWidth: 2,
                                borderStyle: 'solid',
                                backgroundColor: 'transparent',
                                color: theme.palette.text.primary,
                            }}
                        />
                    </Container>
                    <Container style={{ alignItems: 'center', gap: 4 }}>
                        <RoomIcon
                            style={{
                                fontSize: 14,
                            }}
                        />
                        <span
                            style={{
                                ...typographyStyle(theme.typography.caption),
                            }}
                        >
                            Bldg {suggestion.source_unit.building_name} / Unit{' '}
                            {suggestion.source_unit.unit_name}
                        </span>
                    </Container>
                </div>
            </Container>

            {/* Body — Two Column Layout */}
            <div
                style={{
                    display: 'flex',
                    padding: theme.spacing(1.5, 2),
                    gap: theme.spacing(2),
                }}
            >
                {/* Left Column: Notes + Selectors + Buttons */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(1.5),
                        minWidth: 400,
                        flexShrink: 0,
                    }}
                >
                    {/* Inspector Notes */}
                    {inspectorMessages.length > 0 && (
                        <Tooltip
                            title={
                                noteTruncated
                                    ? inspectorMessages
                                          .map(
                                              (msg) =>
                                                  `${
                                                      msg.sender_name
                                                  } (${formatMessageDate(
                                                      msg.created_date,
                                                  )}): ${msg.content}`,
                                          )
                                          .join('\n')
                                    : ''
                            }
                            placement="bottom-start"
                            interactive
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: theme.spacing(0.5),
                                    backgroundColor:
                                        theme.palette.info.light + '25',
                                    border: `1px solid ${theme.palette.info.main}40`,
                                    borderRadius: theme.shape.borderRadius,
                                    padding: theme.spacing(0.75, 1.25),
                                }}
                            >
                                {inspectorMessages.map((msg, idx) => {
                                    const content =
                                        msg.content.length > MAX_NOTE_LENGTH
                                            ? msg.content.slice(
                                                  0,
                                                  MAX_NOTE_LENGTH,
                                              ) + '...'
                                            : msg.content
                                    return (
                                        <Container
                                            key={idx}
                                            style={{
                                                alignItems: 'flex-start',
                                                gap: theme.spacing(0.75),
                                            }}
                                        >
                                            <MessageIcon
                                                style={{
                                                    fontSize: 16,
                                                    color:
                                                        theme.palette.info.main,
                                                    marginTop: 1,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <div>
                                                <span
                                                    style={{
                                                        ...typographyStyle(
                                                            theme.typography
                                                                .caption,
                                                        ),
                                                        color:
                                                            theme.palette.text
                                                                .secondary,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {msg.sender_name}
                                                    {' — '}
                                                    {formatMessageDate(
                                                        msg.created_date,
                                                    )}
                                                </span>
                                                <span
                                                    style={{
                                                        ...typographyStyle(
                                                            theme.typography
                                                                .body2,
                                                        ),
                                                        fontWeight: 500,
                                                        color:
                                                            theme.palette.text
                                                                .primary,
                                                        lineHeight: 1.4,
                                                        display: 'block',
                                                    }}
                                                >
                                                    {content}
                                                </span>
                                            </div>
                                        </Container>
                                    )
                                })}
                            </div>
                        </Tooltip>
                    )}

                    {/* Service & Vendor Selectors */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Container
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(0.75),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    fontWeight: 600,
                                    color: theme.palette.text.secondary,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Service
                            </span>
                            <TextField
                                select
                                variant="outlined"
                                size="small"
                                value={selectedServiceId ?? ''}
                                onChange={(e) =>
                                    onSelectService(Number(e.target.value))
                                }
                                style={{ minWidth: 200 }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            style: { maxHeight: 250 },
                                        },
                                        autoFocus: false,
                                    },
                                    onOpen: () => setServiceSearch(''),
                                    renderValue: (val) => {
                                        const svc = allServices.find(
                                            (s) => s.id === val,
                                        )
                                        if (!svc) return ''
                                        const imgDetails = getServiceImageDetails(
                                            svc.id,
                                            svc.service_category,
                                        )
                                        return (
                                            <span
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}
                                            >
                                                {imgDetails.icon && (
                                                    <img
                                                        src={imgDetails.icon}
                                                        style={{
                                                            maxHeight: 20,
                                                            maxWidth: 22,
                                                        }}
                                                    />
                                                )}
                                                {svc.name}
                                                {svc.confidence !== null
                                                    ? ` (${Math.round(
                                                          svc.confidence * 100,
                                                      )}%)`
                                                    : ' (-)'}
                                            </span>
                                        )
                                    },
                                }}
                            >
                                <ListSubheader
                                    style={{ backgroundColor: '#fff' }}
                                >
                                    <TextField
                                        size="small"
                                        autoFocus
                                        placeholder="Search services..."
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        value={serviceSearch}
                                        onChange={(e) =>
                                            setServiceSearch(e.target.value)
                                        }
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                </ListSubheader>
                                {filteredServices.map((service) => {
                                    const imgDetails = getServiceImageDetails(
                                        service.id,
                                        service.service_category,
                                    )
                                    return (
                                        <MenuItem
                                            key={service.id}
                                            value={service.id}
                                        >
                                            {imgDetails.icon && (
                                                <img
                                                    src={imgDetails.icon}
                                                    style={{
                                                        maxHeight: 20,
                                                        maxWidth: 22,
                                                        marginRight: 6,
                                                    }}
                                                />
                                            )}
                                            {service.name}
                                            {service.confidence !== null
                                                ? ` (${Math.round(
                                                      service.confidence * 100,
                                                  )}%)`
                                                : ' (-)'}
                                        </MenuItem>
                                    )
                                })}
                            </TextField>
                        </Container>

                        <Container
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(0.75),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    fontWeight: 600,
                                    color: theme.palette.text.secondary,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {myTeamMode ? 'Assignee' : 'Vendor'}
                            </span>

                            {myTeamMode ? (
                                <TextField
                                    select
                                    variant="outlined"
                                    size="small"
                                    value={selectedUserId ?? ''}
                                    onChange={(e) =>
                                        onSelectUser(Number(e.target.value))
                                    }
                                    style={{ minWidth: 200 }}
                                    disabled={userList.length === 0}
                                    SelectProps={{
                                        renderValue: (val) => {
                                            const u = userList.find(
                                                (user) => user.id === val,
                                            )
                                            if (!u) return ''
                                            return (
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                    }}
                                                >
                                                    <PeopleIcon
                                                        style={{
                                                            fontSize: 18,
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                        }}
                                                    />
                                                    {u.name}
                                                </span>
                                            )
                                        },
                                    }}
                                >
                                    {userList.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            <PeopleIcon
                                                style={{
                                                    fontSize: 18,
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                    marginRight: 6,
                                                }}
                                            />
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : (
                                <TextField
                                    select
                                    variant="outlined"
                                    size="small"
                                    value={
                                        allVendorsForService.some(
                                            (v) => v.id === selectedVendorId,
                                        )
                                            ? selectedVendorId
                                            : ''
                                    }
                                    onChange={(e) =>
                                        onSelectVendor(Number(e.target.value))
                                    }
                                    style={{ minWidth: 200 }}
                                    disabled={allVendorsForService.length === 0}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: {
                                                style: { maxHeight: 250 },
                                            },
                                            autoFocus: false,
                                        },
                                        onOpen: () => setVendorSearch(''),
                                        renderValue: (val) => {
                                            const v = allVendorsForService.find(
                                                (vn) => vn.id === val,
                                            )
                                            if (!v) return ''
                                            const VendorIcon = v.in_house
                                                ? HomeIcon
                                                : AllOutIcon
                                            return (
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                    }}
                                                >
                                                    <VendorIcon
                                                        style={{
                                                            fontSize: 18,
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                        }}
                                                    />
                                                    {v.name}
                                                    {v.confidence !== null
                                                        ? ` (${Math.round(
                                                              v.confidence *
                                                                  100,
                                                          )}%)`
                                                        : ' (-)'}
                                                </span>
                                            )
                                        },
                                    }}
                                >
                                    <ListSubheader
                                        style={{ backgroundColor: '#fff' }}
                                    >
                                        <TextField
                                            size="small"
                                            autoFocus
                                            placeholder="Search vendors..."
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            value={vendorSearch}
                                            onChange={(e) =>
                                                setVendorSearch(e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                e.stopPropagation()
                                            }
                                        />
                                    </ListSubheader>
                                    {filteredVendors.map((vendor) => {
                                        const VendorIcon = vendor.in_house
                                            ? HomeIcon
                                            : AllOutIcon
                                        return (
                                            <MenuItem
                                                key={vendor.id}
                                                value={vendor.id}
                                            >
                                                <VendorIcon
                                                    style={{
                                                        fontSize: 18,
                                                        color:
                                                            theme.palette.text
                                                                .secondary,
                                                        marginRight: 6,
                                                    }}
                                                />
                                                {vendor.name}
                                                {vendor.confidence !== null
                                                    ? ` (${Math.round(
                                                          vendor.confidence *
                                                              100,
                                                      )}%)`
                                                    : ' (-)'}
                                            </MenuItem>
                                        )
                                    })}
                                </TextField>
                            )}

                            <Button
                                variant={myTeamMode ? 'contained' : 'outlined'}
                                color={myTeamMode ? 'primary' : 'default'}
                                size="small"
                                startIcon={<PeopleIcon />}
                                onClick={onToggleMyTeam}
                                style={{
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                My Team
                            </Button>
                        </Container>
                    </div>

                    {/* Accept / Reject */}
                    {effectiveDecision === 'pending' && (
                        <div
                            style={{
                                display: 'flex',
                                gap: theme.spacing(1),
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={onReject}
                                onMouseEnter={() => setHoveredButton('reject')}
                                onMouseLeave={() => setHoveredButton(null)}
                                style={{
                                    flex: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor:
                                        theme.palette.error.main + '60',
                                    color: theme.palette.error.dark,
                                    backgroundColor:
                                        hoveredButton === 'reject'
                                            ? theme.palette.error.main + '20'
                                            : undefined,
                                    transition: 'background-color 0.15s ease',
                                }}
                            >
                                Reject
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CheckCircleIcon />}
                                onClick={onAccept}
                                onMouseEnter={() => setHoveredButton('accept')}
                                onMouseLeave={() => setHoveredButton(null)}
                                style={{
                                    flex: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor:
                                        theme.palette.success.main + '60',
                                    color: theme.palette.success.dark,
                                    backgroundColor:
                                        hoveredButton === 'accept'
                                            ? theme.palette.success.main + '20'
                                            : undefined,
                                    transition: 'background-color 0.15s ease',
                                }}
                            >
                                Accept
                            </Button>
                        </div>
                    )}
                </div>

                {/* Right Column: Photos Only */}
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        gap: theme.spacing(1),
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        flexWrap: 'nowrap',
                        overflow: 'auto',
                        minWidth: 0,
                    }}
                >
                    {photoUrls.length > 0 && (
                        <>
                            {visiblePhotos.map((url, idx) =>
                                photoThumbnail(url, idx),
                            )}
                            {hasOverflowPhotos && !photosExpanded && (
                                <Tooltip
                                    title={`Show all ${photoUrls.length} photos`}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => setPhotosExpanded(true)}
                                        style={{
                                            width: PHOTO_WIDTH,
                                            height: PHOTO_HEIGHT,
                                            borderRadius:
                                                theme.shape.borderRadius,
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor:
                                                theme.palette.grey[100],
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 4,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <PhotoLibraryIcon
                                            style={{
                                                fontSize: 28,
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        />
                                        <span
                                            style={{
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            +{overflowCount}
                                        </span>
                                    </IconButton>
                                </Tooltip>
                            )}
                            {photosExpanded && (
                                <Tooltip title="Collapse photos">
                                    <IconButton
                                        size="small"
                                        onClick={() => setPhotosExpanded(false)}
                                        style={{
                                            width: 32,
                                            height: 32,
                                            backgroundColor:
                                                theme.palette.grey[200],
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <CloseIcon style={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
